import { useEffect, useState } from 'react';
import { Badge, Button, Dialog } from '@lognai/synapse-design-system-v2';
import { Check, Plus } from '@lognai/synapse-design-system-v2/icons';
import type { WorkspaceCardDefinition, WorkspaceCardId } from './workspace-layout';
import { workspaceModuleStyle, workspaceToneClasses } from './workspace-module-style';
import { WorkspaceAiButton, WorkspaceAiCreator, type WorkspaceAiOptions } from './WorkspaceAiCreator';

export function WorkspaceCardPicker({
  open,
  onOpenChange,
  definitions,
  alreadyAdded,
  onSelect,
  ai,
  startWithAi = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definitions: readonly WorkspaceCardDefinition[];
  alreadyAdded: readonly WorkspaceCardId[];
  onSelect: (id: WorkspaceCardId) => void;
  ai: WorkspaceAiOptions;
  startWithAi?: boolean;
}) {
  const [aiMode, setAiMode] = useState(false);
  useEffect(() => { setAiMode(open && startWithAi); }, [open, startWithAi]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className={`flex max-h-[88vh] flex-col ${aiMode ? 'sm:max-w-5xl' : 'sm:max-w-2xl'}`}>
        {aiMode ? <WorkspaceAiCreator kind="card" {...ai} onBack={startWithAi ? undefined : () => setAiMode(false)} onClose={() => onOpenChange(false)} /> : <>
        <Dialog.Header>
          <Dialog.Title>Add a card</Dialog.Title>
          <Dialog.Description>
            Choose a card for your workspace. Cards you remove can be added again here.
          </Dialog.Description>
        </Dialog.Header>
        <WorkspaceAiButton onClick={() => setAiMode(true)} />
        <div className="grid min-h-0 gap-3 overflow-y-auto sm:grid-cols-2">
          {definitions.map((definition) => {
            const added = alreadyAdded.includes(definition.id);
            const appearance = workspaceModuleStyle(definition.module);
            const Icon = appearance.icon;
            return (
              <button
                key={definition.id}
                type="button"
                disabled={added}
                aria-label={`Add ${definition.title}`}
                onClick={() => onSelect(definition.id)}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-4 text-left transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${workspaceToneClasses[appearance.tone]}`}>
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">{definition.module}</p>
                  <p className="text-sm font-semibold text-foreground">{definition.title}</p>
                </div>
                {added ? (
                  <Badge variant="secondary" className="shrink-0">
                    <Check className="h-3 w-3" /> Added
                  </Badge>
                ) : (
                  <Plus className="h-4 w-4 shrink-0 text-primary" />
                )}
              </button>
            );
          })}
          {definitions.length === 0 && (
            <p className="text-sm text-muted-foreground sm:col-span-2">
              No cards are available for your current company and permissions.
            </p>
          )}
        </div>
        <Dialog.Footer>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </Dialog.Footer>
        </>}
      </Dialog.Content>
    </Dialog>
  );
}
